import { Feature, FeatureCollection, Point } from "geojson";
import maplibregl from "maplibre-gl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LngLatBoundsLike } from "react-map-gl/dist/esm/types";
import { Map as Maplibre, Marker, Popup, useMap } from "react-map-gl/maplibre";

import { useGetCurrentMap } from "~map-view/queries/useGetCurrentMap";

import {
	useDrawingMode,
	useSetActive,
	useSetSelectFeatureId,
	useSetSketchGeojson,
	useSketchGeojson,
} from "~map-view/states/sketch";
import { Layer as LayerType } from "~map-view/types/__generated/gql/graphql";

import { MapImagePlugin } from "./image";
import { Layer } from "./layer";
import { BASEMAPS_TILE, BaseMapTile } from "config/constants";
import { useGetCustomMakerIcons } from "~map-editor/queries/useGetCustomMarkerIcons/useGetCustomMarkerIcons";

type MarkerPosition = [number, number];
type MarkerPositions = Record<string, MarkerPosition>;

export const Map = () => {
	const popupRef = useRef<maplibregl.Popup>();
	const { default: mapRef } = useMap();

	const setActive = useSetActive();
	const setSelectFeatureId = useSetSelectFeatureId();
	const sketchGeojson = useSketchGeojson();
	const setSketchGeojson = useSetSketchGeojson();
	const [markerPositions, setMarkerPositions] = useState<MarkerPositions>({});
	const [mapStyle, setMapStyle] = useState<string>(
		"/new-mangomap-assets/map/light-map.json",
	);
	const drawingMode = useDrawingMode();
	const { data: map } = useGetCurrentMap();
	const { data: makerIcons = [] } = useGetCustomMakerIcons();

	const layers = useMemo(() => {
		if (!map) return [];

		return map.layerGroupCollection.items
			.filter((g) => g.visible)
			.reverse()
			.map((group) =>
				group.layerCollection.items.filter((g) => g.visible).reverse(),
			)
			.flat();
	}, [map]);

	const bounds = useMemo<LngLatBoundsLike | undefined>(() => {
		if (!map) return undefined;

		const bboxes =
			map.layerGroupCollection.items
				.map((g) =>
					g.layerCollection.items.map((l) => l._dataset).map((l) => l.bbox),
				)
				.flat() ?? [];

		if (!bboxes.length) return undefined;

		return [
			Math.min(...bboxes.map((b) => b[0]!)),
			Math.min(...bboxes.map((b) => b[1]!)),
			Math.max(...bboxes.map((b) => b[2]!)),
			Math.max(...bboxes.map((b) => b[3]!)),
		];
	}, [map]);

	useEffect(() => {
		popupRef.current?.trackPointer();
	}, []);

	const filteredMarkerFeatures = useMemo(() => {
		return {
			type: "FeatureCollection",
			features:
				sketchGeojson?.features.filter((feature) => {
					return (
						feature.geometry.type === "Point" &&
						feature.properties?.type === "marker"
					);
				}) ?? [],
		};
	}, [sketchGeojson]);

	const filteredAnnotationFeatures = useMemo<FeatureCollection>(() => {
		return {
			type: "FeatureCollection",
			features:
				sketchGeojson?.features.filter((feature) => {
					return (
						feature.geometry.type === "Point" &&
						feature.properties?.type === "annotation"
					);
				}) ?? [],
		};
	}, [sketchGeojson]);

	const handleMarkerDragStart = useCallback((event, feature) => {
		const featureId = feature.id;
		setSelectFeatureId(featureId);
		setActive("marker");

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handlePopupClick = useCallback((feature) => {
		const featureId = feature.id;
		setSelectFeatureId(featureId);
		setActive("text-annotation");

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleMarkerDragEnd = useCallback(
		(event, feature) => {
			const { lngLat } = event;
			setMarkerPositions((prevPositions) => ({
				...prevPositions,
				[feature.id]: [lngLat.lng, lngLat.lat] as MarkerPosition,
			}));
			const featureId = feature.id;
			const updatedFeatures: Feature[] =
				sketchGeojson?.features.map((feature) => {
					if (feature.id === featureId.trim()) {
						feature.geometry = {
							...feature.geometry,
							type: "Point",
							coordinates: [
								parseFloat(lngLat.lng.toFixed(9)),
								parseFloat(lngLat.lat.toFixed(9)),
							],
						};
					}
					return feature;
				}) ?? [];

			const updatedFeatureCollection: FeatureCollection = {
				...sketchGeojson,
				type: "FeatureCollection",
				features: updatedFeatures,
			};

			setSketchGeojson(updatedFeatureCollection);
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[sketchGeojson],
	);

	useEffect(() => {
		if (map?.baseMapId) {
			const tile = getBaseMapById(map.baseMapId);
			if (tile) setMapStyle(`${tile.styleUrl}8cFp8nJt9vZ2cafyeMm9`);
		}
	}, [map?.baseMapId]);

	useEffect(() => {
		const map = mapRef?.getMap();
		if (!map) return;

		makerIcons.forEach((icon) => {
			if (!map.hasImage(icon.name)) {
				const img = new Image();
				img.crossOrigin = "anonymous";
				img.src = icon.iconUrl;

				img.onload = () => {
					if (!map.hasImage(icon.name)) {
						map.addImage(icon.name, img);
					}
				};
			}
		});
	}, [makerIcons, mapRef]);

	if (!map) return null;

	return (
		<Maplibre
			reuseMaps
			attributionControl={false}
			mapLib={maplibregl}
			dragRotate={false}
			pitchWithRotate={false}
			mapStyle={mapStyle}
			initialViewState={{
				bounds,
				fitBoundsOptions: { padding: 16 },
			}}>
			{layers.map((layer) => {
				return (
					<Layer
						key={layer.id}
						{...(layer as LayerType)}
						allLayers={layers}
					/>
				);
			})}
			{filteredMarkerFeatures.features.map((feature: Feature) => {
				if (!isPoint(feature.geometry)) {
					return null;
				}

				const featureId = String(feature.id);
				const position: MarkerPosition =
					markerPositions[featureId] ??
					(feature.geometry.coordinates as MarkerPosition);

				return (
					<Marker
						key={featureId}
						draggable={drawingMode === "select"}
						latitude={position[1]}
						longitude={position[0]}
						offset={[0, -16]}
						onDragStart={(event) => handleMarkerDragStart(event, feature)}
						onDragEnd={(event) => handleMarkerDragEnd(event, feature)}>
						<img
							src={`/images/markers/${feature.properties?.icon?.options?.iconUrl}`}
							alt="marker"
						/>
					</Marker>
				);
			})}

			{filteredAnnotationFeatures.features.map((feature) => {
				if (!isPoint(feature.geometry)) {
					return null;
				}

				return (
					<Popup
						className="popup-content"
						key={feature.id}
						closeOnClick={false}
						latitude={feature.geometry?.coordinates[1] ?? 0}
						longitude={feature.geometry?.coordinates[0] ?? 0}
						anchor="bottom"
						// eslint-disable-next-line @typescript-eslint/no-empty-function
						onClose={() => {}}>
						<div
							onClick={() =>
								drawingMode === "select" && handlePopupClick(feature)
							}
							style={{ padding: "16px" }}
							dangerouslySetInnerHTML={{
								__html: feature.properties?.annotation?.content,
							}}></div>
					</Popup>
				);
			})}
			<MapImagePlugin />
		</Maplibre>
	);
};

const isPoint = (geometry: any): geometry is Point => geometry.type === "Point";

const getBaseMapById = (basemap_id: string): BaseMapTile | undefined => {
	return BASEMAPS_TILE.find((t) => t.id === basemap_id);
};
